import { STORAGE_KEY } from '../config/constants';
import { getLevel } from '../data/levels';
import { LevelResult } from '../types';
import { calculateStars, isLevelPassed } from './Scoring';

interface Settings {
  musicOn: boolean;
  sfxOn: boolean;
}

interface SaveShape {
  version: 2;
  levelResults: Record<number, LevelResult>;
  unlockedLevelId: number;
  settings: Settings;
}

function defaultSave(): SaveShape {
  return { version: 2, levelResults: {}, unlockedLevelId: 1, settings: { musicOn: true, sfxOn: true } };
}

function migrateLegacySave(raw: Partial<Omit<SaveShape, 'version'>>): SaveShape {
  const fallback = defaultSave();
  const levelResults: Record<number, LevelResult> = {};

  for (const [key, storedResult] of Object.entries(raw.levelResults ?? {})) {
    const levelId = Number(key);
    if (!Number.isInteger(levelId) || !storedResult) continue;
    try {
      const level = getLevel(levelId);
      levelResults[levelId] = {
        levelId,
        score: storedResult.score,
        stars: calculateStars(storedResult.score, level.starThresholds),
        bestCombo: storedResult.bestCombo
      };
    } catch {
      // Ignore results for levels that no longer exist.
    }
  }

  let unlockedLevelId = 1;
  while (isLevelPassed(levelResults[unlockedLevelId]?.stars ?? 0)) {
    unlockedLevelId++;
  }

  return {
    version: 2,
    levelResults,
    unlockedLevelId,
    settings: { ...fallback.settings, ...raw.settings }
  };
}

function load(): SaveShape {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSave();
    const parsed = JSON.parse(raw) as Partial<SaveShape>;
    if (parsed.version !== 2) {
      const migrated = migrateLegacySave(parsed);
      save(migrated);
      return migrated;
    }
    return {
      ...defaultSave(),
      ...parsed,
      settings: { ...defaultSave().settings, ...parsed.settings }
    } as SaveShape;
  } catch {
    return defaultSave();
  }
}

function save(data: SaveShape): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage unavailable (private browsing, quota) — progress just won't persist.
  }
}

export const SaveData = {
  getUnlockedLevelId(): number {
    return load().unlockedLevelId;
  },

  getLevelResult(levelId: number): LevelResult | undefined {
    return load().levelResults[levelId];
  },

  recordLevelResult(result: LevelResult): void {
    const data = load();
    const existing = data.levelResults[result.levelId];
    data.levelResults[result.levelId] = existing
      ? {
          levelId: result.levelId,
          score: Math.max(existing.score, result.score),
          stars: Math.max(existing.stars, result.stars),
          bestCombo: Math.max(existing.bestCombo, result.bestCombo)
        }
      : result;

    if (isLevelPassed(result.stars) && result.levelId >= data.unlockedLevelId) {
      data.unlockedLevelId = result.levelId + 1;
    }
    save(data);
  },

  getSettings(): Settings {
    return load().settings;
  },

  setMusicOn(musicOn: boolean): void {
    const data = load();
    data.settings.musicOn = musicOn;
    save(data);
  },

  setSfxOn(sfxOn: boolean): void {
    const data = load();
    data.settings.sfxOn = sfxOn;
    save(data);
  }
};
